import { K8sResource } from "./types";
import { extractVersionFromResource } from "./utils/github";

export type ResourceRenderFunction = (
    resource: K8sResource
) => string | number | undefined;

interface ResourceKind {
    path: (namespace?: string) => string;
    hidden?: boolean;
    columns: Record<string, ResourceRenderFunction>;
}

const Kinds: Record<string, ResourceKind> = {
    Namespaces: {
        path: () => "/api/v1/namespaces",
        hidden: true,
        columns: {
            Name: resource => resource?.metadata?.name,
        },
    },
    Deployments: {
        path: (namespace?: string) =>
            `/apis/apps/v1${
                namespace && `/namespaces/${namespace}`
            }/deployments`,
        columns: {
            Namespace: resource => resource?.metadata?.namespace,
            Name: resource => resource?.metadata?.name,
            Version: resource => extractVersionFromResource(resource) || "N/A",
            Containers: resource =>
                (resource?.spec?.template?.spec?.containers ?? [])
                    .map(container => container?.name)
                    .join(","),
            Replicas: resource =>
                `${resource?.status?.readyReplicas ?? 0}/${
                    resource?.spec?.replicas
                }`,
        },
    },
    Pods: {
        path: (namespace?: string) =>
            `/api/v1${namespace && `/namespaces/${namespace}`}/pods`,
        columns: {
            Namespace: resource => resource?.metadata?.namespace,
            Name: resource => resource?.metadata?.name,
            Version: resource => extractVersionFromResource(resource) || "N/A",
            Containers: resource =>
                (resource?.spec?.containers ?? [])
                    .map(container => container?.name)
                    .join(","),
            Status: resource => resource?.status?.phase,
        },
    },
    Services: {
        path: (namespace?: string) =>
            `/api/v1${namespace && `/namespaces/${namespace}`}/services`,
        columns: {
            Namespace: resource => resource?.metadata?.namespace,
            Name: resource => resource?.metadata?.name,
            "Target Port": resource =>
                (resource?.spec?.ports ?? [])
                    .map(port => port?.targetPort)
                    .join(","),
            Port: resource =>
                (resource?.spec?.ports ?? []).map(port => port?.port).join(","),
        },
    },
    CronJobs: {
        path: (namespace?: string) =>
            `/apis/batch/v1${namespace && `/namespaces/${namespace}`}/cronjobs`,
        columns: {
            Namespace: resource => resource?.metadata?.namespace,
            Name: resource => resource?.metadata?.name,
            Schedule: resource => resource?.spec?.schedule,
            Enabled: resource => (resource?.spec?.suspend ? "No" : "Yes"),
            TimeZone: resource => resource?.spec?.timeZone,
            "Last Successful Run": resource =>
                resource?.status?.lastSuccessfulTime,
        },
    },
    ConfigMaps: {
        path: (namespace?: string) =>
            `/api/v1${namespace && `/namespaces/${namespace}`}/configmaps`,
        columns: {
            Namespace: resource => resource?.metadata?.namespace,
            Name: resource => resource?.metadata?.name,
            Values: resource =>
                (resource?.data && Object.keys(resource.data).length) || 0,
        },
    },
};

export type { ResourceKind };
export { Kinds };
